import type { Message } from "@nexus/types"

import { getEmbeddedChatMessages } from "./chatApi"
import { isConversationClosed } from "./embed-request-error"
import { embedApi } from "./embedApi"

/** The chat a widget session resumes into, with the newest page of its history. */
export interface ResolvedEmbedChatroom {
  readonly chatId: string
  readonly isNewChatroom: boolean
  readonly topic: string | null
  /** Chronological, oldest first. Empty for a reserved (new) chat. */
  readonly messages: Message[]
  readonly hasMoreHistory: boolean
}

interface ResolveEmbedChatroomInput {
  readonly deploymentId: string
  readonly sessionId: string
  readonly agentId: string
}

/**
 * Resume a widget session: ask the backend which chat the session is in, then read
 * that chat's newest history page.
 *
 * The resume never hands out a DELETED chat, but the chat it does hand out can be
 * deleted before its history is read, and that read then answers
 * `CONVERSATION_CLOSED`. So a closed history read resumes ONE more time — the
 * second resume excludes the chat that was just deleted — and anything the second
 * attempt throws propagates. There is no third attempt.
 *
 * Shared by `initializeEmbedChat` (the mount) and `reopenClosedEmbedChat` (a tab
 * whose conversation was deleted under it), so both resolve a session identically.
 */
export async function resolveEmbedChatroom(
  input: ResolveEmbedChatroomInput
): Promise<ResolvedEmbedChatroom> {
  try {
    return await resumeWithHistory(input)
  } catch (error) {
    if (!isConversationClosed(error)) throw error
    return resumeWithHistory(input)
  }
}

async function resumeWithHistory({
  deploymentId,
  sessionId,
  agentId
}: ResolveEmbedChatroomInput): Promise<ResolvedEmbedChatroom> {
  const { chatroom, isNew } = await embedApi.getChatroom({ deploymentId, sessionId })
  const resumed = { chatId: chatroom.id, isNewChatroom: isNew, topic: chatroom.topic ?? null }

  // A reserved id has no `Chat` row yet, so it has no history to read.
  if (isNew) return { ...resumed, messages: [], hasMoreHistory: false }

  const page = await getEmbeddedChatMessages({
    chatRoomId: chatroom.id,
    agentId,
    sessionId,
    deploymentId,
    limit: "20",
    sortBy: "createdAt",
    order: "desc" // newest first, reversed below to render oldest first
  })

  // debt: the history route's rows are typed `unknown[]` and read here as `Message`
  //       unvalidated — no Zod schema for a message row exists in @nexus/types.
  //       Upgrade trigger: a `MessageSchema` landing there; parse element-wise then.
  return {
    ...resumed,
    messages: (page.messages as Message[]).reverse(),
    hasMoreHistory: page.hasMore
  }
}
